import axios, { AxiosError } from 'axios'
import type { JsonValue } from '@bufbuild/protobuf';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { tryParseEnvelopeError } from './errors'
import { useDeploymentStore } from '@/store/deployment.store'
import { getLoginPath } from '@/pages/auth/loginMode'
import { redirectToOidcLogin } from '@/pages/auth/oidc'
import { usePermissionDeniedStore } from '@/store/permissionDenied.store'
import { useRequestErrorStore } from '@/store/requestError.store'
import { clearAuthTokenCookie } from '@/utils/authCookie'
import { getBearerToken } from '@/utils/authToken'
import { getApiMockAdapter, type ApiMockService } from './mock'

const DEFAULT_TIMEOUT = 30000

interface CreateRequestOptions {
  baseURL: string;
  mockService: ApiMockService;
  parseHttpV2Envelope?: boolean;
  parseIamEnvelope?: boolean;
  skipUnauthorizedRedirect?: (config: InternalAxiosRequestConfig | undefined) => boolean;
}

const HTTP_ERROR_MESSAGES: Readonly<Record<number, string>> = {
  400: '请求参数有误',
  404: '资源不存在或已被删除',
  409: '资源冲突，请刷新后重试',
  429: '请求过于频繁，请稍后重试',
  500: '服务器开小差了，请稍后重试',
  502: '服务暂时不可用，请稍后重试',
  503: '服务暂时不可用，请稍后重试',
  504: '请求超时，请稍后重试',
};

function clearAuthentication(): void {
  localStorage.removeItem('auth-storage')
  clearAuthTokenCookie()
}

function redirectToConfiguredLogin(): void {
  const mode = useDeploymentStore.getState().profile.login.mode
  if (mode === 'oidc') {
    redirectToOidcLogin()
    return
  }
  window.location.replace(getLoginPath(mode))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** 同时兼容 IAM `{ code: 401 }` 与 httpv2 envelope 的鉴权失效状态码。 */
function isUnauthorizedResponse(data: unknown): boolean {
  if (!isRecord(data)) {
    return false
  }
  return data.code === 401 || data.code === '401' || data.http_status_code === 401
}

/** 同时兼容 IAM `{ code: 403 }` 与 httpv2 envelope 的业务状态码。 */
function isPermissionDeniedResponse(data: unknown): boolean {
  if (!isRecord(data)) {
    return false
  }
  return data.code === 403 || data.code === '403' || data.http_status_code === 403
}

function showPermissionDenied(): void {
  usePermissionDeniedStore.getState().show()
}

function showRequestError(message: string): void {
  useRequestErrorStore.getState().show(message);
}

function getResponseMessage(data: unknown): string | undefined {
  if (!isRecord(data)) {
    return undefined;
  }
  return typeof data.message === 'string' && data.message.trim() ? data.message.trim() : undefined;
}

export function getNonAxiosErrorMessage(error: unknown, fallback = '请求失败，请稍后重试'): string {
  if (!(error instanceof Error)) {
    return fallback;
  }
  const responseMessage =
    isRecord(error) && isRecord(error.response)
      ? getResponseMessage(error.response.data)
      : undefined;
  return responseMessage || error.message || fallback;
}

function rejectIamBusinessError(data: unknown): Promise<never> | undefined {
  if (!isRecord(data) || data.code === undefined || data.code === 0 || data.code === '0') {
    return undefined;
  }
  const status = Number(data.http_status_code ?? data.code);
  const error = new Error(
    (Number.isFinite(status) ? HTTP_ERROR_MESSAGES[status] : undefined) ||
      getResponseMessage(data) ||
      '请求失败，请稍后重试'
  );
  showRequestError(error.message);
  return Promise.reject(error);
}

function getAxiosErrorMessage(error: AxiosError): string {
  if (error.code === AxiosError.ECONNABORTED) {
    return '请求超时，请稍后重试';
  }
  if (!error.response) {
    return '网络请求失败，请检查网络连接';
  }
  return (
    HTTP_ERROR_MESSAGES[error.response.status] ||
    getResponseMessage(error.response.data) ||
    error.message ||
    '请求失败，请稍后重试'
  );
}

class PermissionDeniedError extends Error {
  constructor() {
    super('暂无操作权限');
    this.name = 'PermissionDeniedError';
  }
}

/** 弹窗展示 403 后仍结束请求生命周期，由页面静默消费该错误以释放 Loading。 */
function rejectPermissionDeniedResponse(): Promise<never> {
  showPermissionDenied();
  return Promise.reject(new PermissionDeniedError());
}

function rejectUnauthorizedResponse(response: AxiosResponse): Promise<never> {
  return Promise.reject(
    new AxiosError(
      '登录已失效，请重新登录',
      AxiosError.ERR_BAD_RESPONSE,
      response.config,
      response.request,
      response
    )
  )
}

/**
 * 创建使用平台统一 Axios 配置与拦截器的请求实例。
 *
 * 不同服务只通过配置声明 baseURL 和响应协议差异；通用鉴权、FormData、错误日志及
 * 401 鉴权失效与 403 无权限处理均在此收口，后续新增统一 response code 逻辑也只需修改这一处。
 */
export function createRequest({
  baseURL,
  mockService,
  parseHttpV2Envelope = false,
  skipUnauthorizedRedirect,
  parseIamEnvelope = false,
}: CreateRequestOptions): AxiosInstance {
  const request = axios.create({
    baseURL,
    timeout: DEFAULT_TIMEOUT,
    headers: {
      'Content-Type': 'application/json'
    }
  })

  request.interceptors.request.use(
    config => {
      if (config.data instanceof FormData) {
        config.headers.delete('Content-Type')
      }

      const token = getBearerToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      const mockAdapter = getApiMockAdapter(mockService, config)
      if (mockAdapter) {
        config.adapter = mockAdapter
      }
      return config
    },
    (error: unknown) => {
      console.error('[API Request] Error:', error,);
      showRequestError(
        axios.isAxiosError(error)
          ? getAxiosErrorMessage(error)
          : error instanceof Error
            ? error.message
            : '请求失败，请稍后重试'
      );
      return Promise.reject(error);
    }
  )

  request.interceptors.response.use(
    (response) => {
      const shouldSkipRedirect = skipUnauthorizedRedirect?.(response.config) ?? false;
      const unauthorized = isUnauthorizedResponse(response.data);

      const permissionDenied = isPermissionDeniedResponse(response.data)
      if (permissionDenied) {
        return rejectPermissionDeniedResponse();
      }

      // HTTP 200 + 401 body 仍是失败响应，避免旧接口调用方在页面跳转前把它当作成功数据处理。
      if (unauthorized) {
        if (!shouldSkipRedirect) {
          clearAuthentication();
          redirectToConfiguredLogin();
        }
        return rejectUnauthorizedResponse(response);
      }

      if (parseHttpV2Envelope) {
        const apiError = tryParseEnvelopeError(response.data as JsonValue);
        if (apiError) {
          showRequestError(apiError.message);
          return Promise.reject(apiError);
        }
      }

      if (parseIamEnvelope) {
        const iamError = rejectIamBusinessError(response.data);
        if (iamError) {
          return iamError;
        }
      }

      return response;
    },
    (error: unknown) => {
      if (axios.isAxiosError(error)) {
        console.error('[API Response] Error:', error.response?.status, error.message)
        if (error.response?.status === 403 || isPermissionDeniedResponse(error.response?.data)) {
          return rejectPermissionDeniedResponse();
        }
        const shouldSkipRedirect = skipUnauthorizedRedirect?.(error.config) ?? false
        const unauthorized =
          error.response?.status === 401 || isUnauthorizedResponse(error.response?.data)
        if (unauthorized && !shouldSkipRedirect) {
          clearAuthentication()
          redirectToConfiguredLogin()
        }
        if (!unauthorized) {
          showRequestError(getAxiosErrorMessage(error));
        }
      } else {
        showRequestError(getNonAxiosErrorMessage(error));
      }
      return Promise.reject(error)
    }
  )

  return request
}

export const baseURL = import.meta.env.VITE_API_BASE_URL || '/api'
const iamBaseURL = import.meta.env.VITE_IAM_API_BASE_URL || '/api/iam'

export const apiClient = createRequest({
  baseURL,
  mockService: 'api',
  parseHttpV2Envelope: true
})

export const iamClient = createRequest({
  baseURL: iamBaseURL,
  mockService: 'iam',
  parseIamEnvelope: true,
  skipUnauthorizedRedirect: (config) => config?.url?.includes('GetAccountToken') ?? false,
});

export const bffBaseURL = (import.meta.env.VITE_BFF_API_BASE_URL ?? '').trim().replace(/\/$/, '');
export const bffClient = createRequest({ baseURL: bffBaseURL, mockService: 'bff' });
